import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  useTelemetryStore,
  getDisplayStatus,
  relativeTime,
  GatewayStatus,
} from "../stores/telemetryStore";
import { useSiteId } from "../hooks/useSiteId";
import { useWarehouseId } from "../hooks/useWarehouseId";
import { useNow } from "../hooks/useNow";
import { api, type BufferCell } from "../api/rest";
import styles from "./NetworkPage.module.css";

const SPARKLINE_WINDOW_MS = 60 * 60 * 1000;

function buildSparkline(
  gaps: { assetId: string; fromMs: number; toMs: number }[],
  assetId: string,
  nowMs: number,
  width: number,
): { rects: { x: number; w: number }[]; uptimePct: number } {
  const windowStart = nowMs - SPARKLINE_WINDOW_MS;
  const relevant = gaps.filter((g) => g.assetId === assetId && g.toMs > windowStart);

  let downMs = 0;
  const rects: { x: number; w: number }[] = [];

  for (const g of relevant) {
    const from = Math.max(g.fromMs, windowStart);
    const to   = Math.min(g.toMs,   nowMs);
    if (to <= from) continue;
    downMs += to - from;
    const x = ((from - windowStart) / SPARKLINE_WINDOW_MS) * width;
    const w = ((to   - from)        / SPARKLINE_WINDOW_MS) * width;
    rects.push({ x, w });
  }

  const uptimePct = Math.max(0, 100 - (downMs / SPARKLINE_WINDOW_MS) * 100);
  return { rects, uptimePct };
}

function GatewayCard({ gw }: { gw: GatewayStatus }) {
  const { t } = useTranslation();
  const p          = gw.peripherals;
  const v2pro      = p?.v2pro;
  const forkSensor = p?.forkSensor;
  const bufFill    = p?.bufferFill ?? 0;

  return (
    <div className={`${styles.gatewayCard} ${gw.online ? "" : styles.offline}`}>
      <div className={styles.gatewayHeader}>
        <span className={styles.gatewayId}>{gw.id}</span>
        <span className={`${styles.badge} ${gw.online ? styles.online : styles.offline}`}>
          {gw.online ? t("network.badge.online") : t("network.badge.offline")}
        </span>
      </div>

      {v2pro !== undefined && (
        <div className={styles.peripheralRow}>
          <span className={styles.peripheralLabel}>V2Pro</span>
          <span className={`${styles.dot} ${v2pro.ok ? styles.ok : styles.fail}`} />
        </div>
      )}

      {forkSensor !== undefined && (
        <div className={styles.peripheralRow}>
          <span className={styles.peripheralLabel}>{t("network.peripheral.forkSensor")}</span>
          <span className={`${styles.dot} ${forkSensor.ok ? styles.ok : styles.fail}`} />
        </div>
      )}

      <div className={styles.peripheralRow}>
        <span className={styles.peripheralLabel}>{t("network.peripheral.buffer")}</span>
        <div className={styles.bufferBar}>
          <div
            className={styles.bufferFill}
            style={{ width: `${Math.min(100, bufFill * 100).toFixed(1)}%` }}
          />
        </div>
      </div>

      <div style={{ fontSize: 11, color: "var(--op-ink-500)", marginTop: 8, fontFamily: "var(--font-mono)" }}>
        {gw.online ? `${gw.assetId ?? "—"}` : t("network.lastSeenPrefix", { time: relativeTime(gw.lastSeenMs) })}
      </div>
    </div>
  );
}

// ── Buffer-age heatmap ────────────────────────────────────────────────────────

const WIDTH_M  = 60;
const HEIGHT_M = 40;
const CELL_M   = 2;
const SVG_W    = 480;
const SVG_H    = (SVG_W / WIDTH_M) * HEIGHT_M; // 320

// Percent of a cell's samples that the edge had to store-and-forward. The stops are
// deliberately steep at the bottom: a cell that buffers even a few percent of the time is
// already an access-point problem, so the useful resolution is all in the first decile.
function bufferColor(pct: number): string {
  const stops: [number, string][] = [
    [0,  "#22d3ee"],
    [5,  "#4ade80"],
    [15, "#fbbf24"],
    [30, "#fb923c"],
    [50, "#f87171"],
  ];
  if (pct <= 0) return stops[0][1];
  if (pct >= stops[stops.length - 1][0]) return stops[stops.length - 1][1];
  for (let i = 1; i < stops.length; i++) {
    const [lo, cLo] = stops[i - 1];
    const [hi, cHi] = stops[i];
    if (pct <= hi) {
      const t = (pct - lo) / (hi - lo);
      const hex = (c: string) => parseInt(c.slice(1), 16);
      const r1 = (hex(cLo) >> 16) & 0xff, g1 = (hex(cLo) >> 8) & 0xff, b1 = hex(cLo) & 0xff;
      const r2 = (hex(cHi) >> 16) & 0xff, g2 = (hex(cHi) >> 8) & 0xff, b2 = hex(cHi) & 0xff;
      const r = Math.round(r1 + (r2 - r1) * t);
      const g = Math.round(g1 + (g2 - g1) * t);
      const b = Math.round(b1 + (b2 - b1) * t);
      return `rgb(${r},${g},${b})`;
    }
  }
  return stops[stops.length - 1][1];
}

function BufferHeatmapPanel({ siteId, hours }: { siteId: string; hours: number }) {
  const { t, i18n } = useTranslation();
  const warehouseId = useWarehouseId();
  const { data: cells = [], isLoading } = useQuery({
    queryKey: ["buffer-heatmap", siteId, warehouseId, hours],
    queryFn: () => api.getBufferHeatmap(siteId, hours, CELL_M, warehouseId),
    refetchInterval: 30_000,
  });

  const scaleX = SVG_W / WIDTH_M;
  const scaleY = SVG_H / HEIGHT_M;
  const cellPx = CELL_M * scaleX;

  return (
    <div className={styles.panel}>
      <h3 className={styles.panelTitle}>{t("network.heatmap.title")}</h3>
      {isLoading ? (
        <div className={styles.skeleton} style={{ width: SVG_W, height: SVG_H }} />
      ) : cells.length === 0 ? (
        <p className={styles.emptyMsg}>{t("network.heatmap.empty")}</p>
      ) : (
        <>
          <svg width={SVG_W} height={SVG_H} className={styles.heatmapSvg}>
            <rect width={SVG_W} height={SVG_H} fill="rgba(255,255,255,0.03)" rx={4} />
            {(cells as BufferCell[]).map((c, i) => (
              <rect
                key={i}
                x={c.gx * scaleX}
                y={c.gz * scaleY}
                width={cellPx - 1}
                height={cellPx - 1}
                fill={bufferColor(c.buffered_pct)}
                opacity={0.75}
                rx={2}
              >
                <title>{t("network.heatmap.cell", {
                  gx: c.gx,
                  gz: c.gz,
                  pct: c.buffered_pct.toLocaleString(i18n.language, { maximumFractionDigits: 1 }),
                  buffered: c.buffered,
                  samples: c.samples,
                })}</title>
              </rect>
            ))}
          </svg>
          <div className={styles.legend}>
            <span className={styles.legendLabel}>{t("network.heatmap.legend.low")}</span>
            <div className={styles.legendBar} />
            <span className={styles.legendLabel}>{t("network.heatmap.legend.high")}</span>
          </div>
        </>
      )}
    </div>
  );
}

// ── Per-asset connectivity strip chart ───────────────────────────────────────

const SEG_COLORS: Record<string, string> = {
  SESSION_LOADED_MOVE: "rgba(30,91,216,0.5)",
  SESSION_EMPTY_MOVE:  "rgba(30,91,216,0.3)",
  SESSION_IDLE:        "rgba(30,91,216,0.2)",
  SESSION_LINK_DOWN:   "#fb923c",
  SESSION_OFFLINE:     "#6b7280",
  DEFAULT:             "rgba(30,91,216,0.4)",
};

function ConnectivityStripChart({ siteId, hours }: { siteId: string; hours: number }) {
  const { t, i18n } = useTranslation();
  const warehouseId = useWarehouseId();
  const { data, isLoading } = useQuery({
    queryKey: ["network-connectivity", siteId, warehouseId, hours],
    queryFn: () => api.getNetworkConnectivity(siteId, hours, warehouseId),
    refetchInterval: 60_000,
  });

  if (isLoading) return (
    <div className={styles.panel}>
      <h3 className={styles.panelTitle}>{t("network.connectivity.title")}</h3>
      <div className={styles.skeleton} style={{ height: 120 }} />
    </div>
  );
  if (!data || data.assets.length === 0) return (
    <div className={styles.panel}>
      <h3 className={styles.panelTitle}>{t("network.connectivity.title")}</h3>
      <p className={styles.emptyMsg}>{t("network.connectivity.empty")}</p>
    </div>
  );

  const totalMs = data.toMs - data.fromMs;
  const stripW  = 480;
  const rowH    = 28;
  const labelW  = 90;
  const chartW  = stripW - labelW;
  const ticks   = Array.from({ length: hours + 1 }, (_, i) =>
    data.fromMs + (i / hours) * totalMs
  );

  return (
    <div className={styles.panel}>
      <h3 className={styles.panelTitle}>{t("network.connectivity.title")}</h3>
      <svg width={stripW} height={data.assets.length * rowH + 20}>
        {ticks.map((ts, i) => {
          const x = labelW + (i / hours) * chartW;
          const label = new Date(ts).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" });
          return (
            <g key={i}>
              <line x1={x} y1={0} x2={x} y2={data.assets.length * rowH} stroke="var(--op-line)" />
              <text x={x} y={data.assets.length * rowH + 14} textAnchor="middle"
                    fontSize={9} fill="var(--op-ink-500)">{label}</text>
            </g>
          );
        })}
        {data.assets.map((asset, row) => {
          const y = row * rowH;
          return (
            <g key={asset.id}>
              <text x={labelW - 6} y={y + rowH * 0.65} textAnchor="end"
                    fontSize={11} fill="var(--op-ink-700)">{asset.id}</text>
              <rect x={labelW} y={y + 4} width={chartW} height={rowH - 8}
                    fill="var(--op-surface-2)" rx={3} />
              {asset.segments.map((seg, si) => {
                const x1 = labelW + Math.max(0, (seg.fromMs - data.fromMs) / totalMs) * chartW;
                const x2 = labelW + Math.min(1, (seg.toMs   - data.fromMs) / totalMs) * chartW;
                const fill = SEG_COLORS[seg.type] ?? SEG_COLORS.DEFAULT;
                return (
                  <rect key={si} x={x1} y={y + 4} width={Math.max(2, x2 - x1)} height={rowH - 8}
                        fill={fill} rx={2}>
                    <title>{`${asset.id} · ${seg.type} · ${new Date(seg.fromMs).toLocaleTimeString(i18n.language)} → ${new Date(seg.toMs).toLocaleTimeString(i18n.language)}`}</title>
                  </rect>
                );
              })}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Top disruptions list ──────────────────────────────────────────────────────

function DisruptionList({ siteId, hours }: { siteId: string; hours: number }) {
  const { t, i18n } = useTranslation();
  const warehouseId = useWarehouseId();
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["disruptions", siteId, warehouseId, hours],
    queryFn: () => api.getDisruptions(siteId, hours, 5, warehouseId),
    refetchInterval: 60_000,
  });
  const select = useTelemetryStore((s) => s.select);
  const maxSec = Math.max(...rows.map(r => r.duration_s), 1);

  const fmt = (s: number) =>
    s >= 60 ? `${Math.floor(s / 60)} min ${s % 60} s` : `${s} s`;

  return (
    <div className={styles.panel}>
      <h3 className={styles.panelTitle}>{t("network.disruptions.title")}</h3>
      {isLoading ? (
        <div className={styles.skeleton} style={{ height: 80 }} />
      ) : rows.length === 0 ? (
        <p className={styles.emptyMsg} style={{ color: "var(--op-success)" }}>✓ {t("network.disruptions.empty")}</p>
      ) : (
        <table className={styles.disruptTable}>
          <thead>
            <tr>
              <th>{t("network.disruptions.col.asset")}</th>
              <th>{t("network.disruptions.col.start")}</th>
              <th>{t("network.disruptions.col.duration")}</th>
              <th style={{ width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} onClick={() => select(r.asset_id)} style={{ cursor: "pointer" }}>
                <td className={styles.mono}>{r.asset_id}</td>
                <td className={styles.mono}>{new Date(r.start_ts).toLocaleTimeString(i18n.language)}</td>
                <td className={styles.mono}>{fmt(r.duration_s)}</td>
                <td>
                  <div className={styles.durationBar}
                       style={{ width: `${Math.round((r.duration_s / maxSec) * 72)}px` }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function NetworkPage() {
  // State-backed clock: pure render (no Date.now() during render) + 5 s refresh.
  const nowMs = useNow(5_000);
  const { t, i18n } = useTranslation();

  const SITE_ID      = useSiteId();
  const gatewayMap   = useTelemetryStore((s) => s.gateways);
  const forkliftMap  = useTelemetryStore((s) => s.forklifts);
  const gaps         = useTelemetryStore((s) => s.gaps);
  const [hours, setHours] = useState(8);

  const gateways  = Object.values(gatewayMap);
  const forklifts = Object.values(forkliftMap);

  const total  = forklifts.length;
  const active = forklifts.filter((f) => getDisplayStatus(f) !== "offline").length;

  const WINDOW = SPARKLINE_WINDOW_MS;

  const gapsByAsset = new Map<string, number>();
  for (const g of gaps) {
    const from = Math.max(g.fromMs, nowMs - WINDOW);
    const to   = Math.min(g.toMs,   nowMs);
    if (to > from) {
      gapsByAsset.set(g.assetId, (gapsByAsset.get(g.assetId) ?? 0) + (to - from));
    }
  }
  const topAssets = [...gapsByAsset.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([id]) => id);

  const SPARK_W = 320;
  const SPARK_H = 14;

  return (
    <div className={styles.page}>
      <h1 className={styles.h1}>{t("network.title")}</h1>
      <p className={styles.sub}>
        {t("network.summary", { active, total, gaps: gaps.length })}
      </p>

      {gateways.length > 0 && (
        <>
          <p className={styles.sectionTitle}>{t("network.gateways")}</p>
          <div className={styles.gatewayRow}>
            {gateways.map((gw) => <GatewayCard key={gw.id} gw={gw} />)}
          </div>
        </>
      )}

      <p className={styles.sectionTitle}>{t("oee.fleet")}</p>
      <table className={styles.table} style={{ marginBottom: 24 }}>
        <thead>
          <tr>
            <th>{t("common.col.asset")}</th>
            <th>{t("common.col.status")}</th>
            <th>{t("common.col.battery")}</th>
            <th>{t("common.col.lastSeen")}</th>
          </tr>
        </thead>
        <tbody>
          {forklifts.length === 0 ? (
            <tr><td colSpan={4} className={styles.empty}>{t("network.fleet.empty")}</td></tr>
          ) : (
            forklifts.map((f) => {
              const st = getDisplayStatus(f);
              return (
                <tr key={f.id}>
                  <td className={styles.mono}>{f.id}</td>
                  <td>
                    <span className={`${styles.pill} ${styles[`pill_${st}`]}`}>{t(`status.${st}`)}</span>
                  </td>
                  <td className={styles.mono}>{Math.round(f.battery * 100)}%</td>
                  <td className={styles.mono}>{relativeTime(f.lastSeenMs)}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      {topAssets.length > 0 && (
        <div className={styles.sparkSection}>
          <p className={styles.sectionTitle}>
            {t("network.spark.title", { count: topAssets.length })}
          </p>
          {topAssets.map((assetId) => {
            const { rects, uptimePct } = buildSparkline(gaps, assetId, nowMs, SPARK_W);
            return (
              <div key={assetId} className={styles.sparkRow}>
                <span className={styles.sparkLabel}>{assetId}</span>
                <svg
                  className={styles.sparkSvg}
                  viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
                  preserveAspectRatio="none"
                >
                  <rect x={0} y={0} width={SPARK_W} height={SPARK_H} fill="rgba(14,31,110,0.3)" rx={2} />
                  {rects.map(({ x, w }, i) => (
                    <rect
                      key={i}
                      x={x}
                      y={0}
                      width={Math.max(2, w)}
                      height={SPARK_H}
                      fill="var(--op-danger,#dc2626)"
                      rx={1}
                    />
                  ))}
                </svg>
                <span className={styles.sparkUptime}>
                  {uptimePct.toFixed(1).replace(".", ",")}%
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 28 }}>
        <p className={styles.sectionTitle}>{t("network.recentGaps")}</p>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{t("common.col.asset")}</th>
              <th>{t("network.disruptions.col.start")}</th>
              <th>{t("network.col.end")}</th>
              <th>{t("network.disruptions.col.duration")}</th>
            </tr>
          </thead>
          <tbody>
            {gaps.length === 0 ? (
              <tr><td colSpan={4} className={styles.empty}>{t("network.recentGaps.empty")}</td></tr>
            ) : (
              gaps.slice().reverse().slice(0, 50).map((g, i) => (
                <tr key={i}>
                  <td className={styles.mono}>{g.assetId}</td>
                  <td className={styles.mono}>{new Date(g.fromMs).toLocaleTimeString(i18n.language)}</td>
                  <td className={styles.mono}>{new Date(g.toMs).toLocaleTimeString(i18n.language)}</td>
                  <td className={styles.mono}>
                    {((g.toMs - g.fromMs) / 1000).toFixed(1).replace(".", ",")} s
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Time-range selector */}
      <div className={styles.rangeRow} style={{ marginTop: 32 }}>
        {([1, 8, 24] as const).map(h => (
          <button
            key={h}
            className={`${styles.rangeBtn}${hours === h ? " " + styles.rangeBtnActive : ""}`}
            onClick={() => setHours(h)}
          >
            {t(`network.range.${h}h`)}
          </button>
        ))}
      </div>

      <BufferHeatmapPanel siteId={SITE_ID} hours={hours} />
      <ConnectivityStripChart siteId={SITE_ID} hours={hours} />
      <DisruptionList siteId={SITE_ID} hours={hours} />
    </div>
  );
}
